export type RecomEnvelope<TData, TMeta = Record<string, never>> = {
	data: TData;
	meta: TMeta;
};

export type RecomIdentity = {
	userId?: string | null;
	sessionId?: string | null;
};

export type RecommendationType =
	| 'outfit'
	| 'recommendations'
	| 'complements'
	| 'personal'
	| 'collaborative'
	| 'hybrid'
	| 'quiz'
	| 'searchtext'
	// Side-types: solo llenan su campo en la respuesta, no `recommendations`, y
	// por sí solos no activan la batería (el upstream pide un primario).
	| 'trending'
	| 'popular'
	| 'recently_viewed'
	// No-primario: viaja siempre en su campo propio (`data.cart`), nunca llena
	// `recommendations` y no activa la batería. Necesita `partnumbers`: sin
	// semilla el sub-run ni corre y la respuesta llega sin el campo.
	| 'cart'
	| 'sameBrand'
	| 'family';

export type RecommendationRuleValues = {
	values: Record<string, unknown>[];
};

export type RecomGenderSegment = 'hombre' | 'mujer' | 'kids';

export type RecommendationParams = {
	storeId: string;
	queries: string[];
	partnumber?: string;
	// Semilla del carrusel del carrito: los partnumbers que el usuario ya tiene
	// dentro, los más recientes primero. El tope es por tienda
	// (`carousels.cart.partnumbers_max`, default 20) y excederlo es un 400 del
	// upstream. Se ignora si `cart` no viene en `type`.
	partnumbers?: string[];
	limit?: number;
	userId?: string;
	sessionId?: string;
	personalizationAlpha?: number;
	useHistory?: boolean;
	// Filtro de género POR LLAMADA: pisa el género que el motor deduce del perfil
	// y del comportamiento. Si dejara un carrusel en cero, el upstream reintenta
	// sin filtro (fail-open), así que un riel segmentado puede traer de todo.
	genderFilter?: RecomGenderSegment[];
	sizes?: RecommendationSizes;
	sizeMode?: RecommendationSizeMode;
};

export type RecommendationSizes = Partial<
	Record<'tops' | 'shirts' | 'pants' | 'footwear' | 'caps', string>
>;

export type RecommendationSizeMode = 'prefer';

export type RecommendationRequest = {
	type: RecommendationType[];
	rules: {
		params: RecommendationParams;
		filters?: RecommendationRuleValues;
		priority?: RecommendationRuleValues;
	};
	skipCuration?: boolean;
};

export type RecommendationScoreBreakdown = {
	image: number;
	text: number;
	combined: number;
};

export type RecommendationItem = {
	id: string;
	similarity: number;
	vectorSource: string;
	scoreBreakdown: RecommendationScoreBreakdown;
	title: string;
	brand: string;
	color: string;
	gender: string;
	productType: string;
	imageLink: string;
	link: string;
};

export type RecommendationOutfitAnchor = {
	id: string;
	title: string;
	price: string;
	imageLink: string;
	link: string;
};

export type RecommendationOutfitItem = RecommendationOutfitAnchor & {
	zone: string;
};

export type RecommendationOutfitCarousel = {
	anchor: RecommendationOutfitAnchor;
	items: RecommendationOutfitItem[];
	score: number;
};

export type RecommendationComplementItem = {
	id: string;
	title: string;
	price: string;
	imageLink: string;
	link: string;
};

export type RecommendationQueried = {
	id: string;
	title: string;
	price: string;
	productType: string;
	link: string;
};

export type RecommendationCarouselConfig = {
	showOutfits?: boolean;
	showComplements?: boolean;
	outfitLabel?: string;
	complementsLabel?: string;
};

export type RecomParallelProfile = {
	segment: RecomGenderSegment;
	score: number;
	events: number;
};

/**
 * Passthrough verbatim del upstream, de ahí el snake_case. Solo está tipado lo
 * que se consume; el bloque trae además `user_sizes`, `price_reference`,
 * `offer_affinity` y `carousels`. Ausente cuando la tienda tiene la
 * personalización apagada (`personalization_weights` en default).
 */
export type RecommendationPersonalization = {
	parallel_profiles?: RecomParallelProfile[];
};

export type RecommendationData = {
	recommendations: RecommendationItem[];
	outfits: RecommendationOutfitCarousel[];
	complements: RecommendationComplementItem[];
	queried: RecommendationQueried;
	domain?: string;
	carouselConfig?: RecommendationCarouselConfig;
	collaborativeMeta?: unknown;
	coldStartCf: boolean;
	// Campos de la batería: el primer primario del request llena `recommendations`
	// y los demás llegan aparte. Ausentes cuando no se pidió ese tipo.
	collaborative?: RecommendationItem[];
	hybrid?: RecommendationItem[];
	quiz?: RecommendationItem[];
	trending?: RecommendationItem[];
	popular?: RecommendationItem[];
	recentlyViewed?: RecommendationItem[];
	sameBrand?: RecommendationItem[];
	family?: RecommendationItem[];
	// El carrusel del carrito: no-primario, así que llega aparte igual que los
	// side-types pero sin relleno hasta `limit`. Ausente cuando no se pidió o la
	// semilla iba vacía; `[]` cuando corrió y ningún partnumber resolvió.
	cart?: RecommendationItem[];
	personalization?: RecommendationPersonalization;
};

export type RecommendationMeta = {
	timestamp: string;
	requestId: string;
	processingTimeMs: number;
	input: {
		type: string[];
		rules: {
			params: Partial<RecommendationParams>;
			filters?: RecommendationRuleValues | string[];
			priority?: RecommendationRuleValues | string[];
		};
	};
	strategy: string;
	warnings: string[];
	coldStart: boolean;
};

export type RecommendationResponse = RecomEnvelope<RecommendationData, RecommendationMeta>;

export type UserEventType =
	| 'view'
	| 'click'
	| 'addToCart'
	| 'purchase'
	| 'search'
	| 'dislike'
	| 'refund'
	| 'favorite'
	| 'size'
	| 'offer'
	// Evento de SECCIÓN: el visitante entró a una sección de un segmento
	// (chips, navegación, filtro de género). Señal-only —no mueve el vector de
	// estilo ni `userEventCount`— y NUNCA lleva partnumber.
	| 'gender';

export type UserEventSizeCategory = 'tops' | 'shirts' | 'pants' | 'footwear' | 'caps';

export type UserEventOfferSource = 'section' | 'product';

export type UserEvent = {
	eventType: UserEventType;
	partnumber?: string;
	query?: string;
	ts?: string;
	sessionId?: string;
	orderId?: string;
	size?: string;
	sizeCategory?: UserEventSizeCategory;
	source?: UserEventOfferSource;
	// Solo en eventos `gender`, y con el vocabulario estricto de `genderFilter`:
	// un valor fuera de él es un 400 del wrapper.
	gender?: RecomGenderSegment;
};

export type RecordUserEventsRequest = RecomIdentity & {
	events: UserEvent[];
};

export type RecordUserEventsData = {
	accepted: number;
	deduplicated: number;
	skippedUnknownPartnumber: string[];
	userEventCount: number;
};

export type RecentlyViewedItem = {
	partnumber: string;
	ts: number;
};

export type RecentlyViewedData = {
	items: RecentlyViewedItem[];
};

export type TrendingProductItem = {
	partnumber: string;
	velocity: number;
	recentScore: number;
	recentViews: number;
	recentPurchases: number;
};

export type TrendingProductsData = {
	items: TrendingProductItem[];
	generatedAt?: number;
};

export type PopularProductItem = {
	partnumber: string;
	score: number;
	viewCount: number;
	purchaseCount: number;
	lastTs: number;
};

export type PopularProductsData = {
	items: PopularProductItem[];
};

export type MergeUserSessionRequest = {
	userId: string;
	sessionId: string;
};

export type MergeUserSessionData = {
	eventsMerged: number;
	guestVectorDiscarded: boolean;
};

export type SubmitUserQuizRequest = {
	userId: string;
	sports: string[];
	brands: string[];
	gender?: string;
	sizes?: unknown;
};

export type SubmitUserQuizData = {
	stored: boolean;
	vectorDimensions: number;
	quizText: string;
};

export type UserProfileShoeSize = {
	value: string;
	sizeSystem: string;
};

export type UserProfileLocation = {
	country: string;
	state?: string;
	city?: string;
	zipCode?: string;
};

export type UserProfileConsent = {
	marketing?: boolean;
	profiling?: boolean;
};

export type UpdateUserProfileRequest = {
	gender?: string;
	birthDate?: string;
	preferredLanguage?: string;
	shoeSize?: UserProfileShoeSize;
	location?: UserProfileLocation;
	consent?: UserProfileConsent;
	attributes?: Record<string, unknown>;
};

export type UpdateUserProfileData = {
	stored: boolean;
	userEventCount: number;
};

export type CouponEvaluateRequest = {
	storeId?: string;
	userId?: string;
	sessionId?: string;
	partnumber?: string;
	cartTotal?: number;
};

export type EvaluatedCouponDiscountType = string;

export const COUPON_DISCOUNT_TYPE = {
	percentOff: 'PERCENT_OFF',
} as const;

export type EvaluatedCouponDiscount = {
	type: EvaluatedCouponDiscountType;
	value: number;
	currency?: string;
};

export type EvaluatedCouponReason = string;

export const COUPON_EVALUATE_REASON = {
	excludedByFilter: 'EXCLUDED_BY_FILTER',
	minAmountNotMet: 'MIN_AMOUNT_NOT_MET',
} as const;

export type EvaluatedCoupon = {
	code: string;
	applies: boolean;
	name: string;
	promotionId: number;
	reason?: EvaluatedCouponReason;
	discount?: EvaluatedCouponDiscount;
	missingAmount?: number;
};

export type CouponEvaluateResult = {
	cartAmount: number;
	coupons: EvaluatedCoupon[];
};

export type CouponEvaluateData = {
	recommend: boolean;
	result?: CouponEvaluateResult | null;
};
